import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckIcon,
  PlusIcon,
  PackageIcon,
  DotsThreeIcon,
  WarningIcon,
  InfoIcon,
  ShoppingCartIcon,
  TruckIcon,
  CurrencyDollarIcon,
  GitDiffIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { orpc } from '@/api/rpc/client';
import {
  useEntityAssociationsList,
  useCreateEntityAssociation,
  useDeleteEntityAssociation,
} from '@/api/rpc/hooks/core/useEntityAssociations';
import {
  SystemEntity,
  EntityAssociationType,
  type ChargeableEntity,
} from '@erp/shared-types';
import { SystemEntityInline } from '@/components/system-entities';
import { usePurchaseOrder } from '@/api/rpc/hooks/accounts-payable/usePurchaseOrders';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import type { PurchaseOrderLineItem, Charge } from '@erp/rpc';

interface InvoiceLineItemDisplay {
  id: string;
  lineNumber: number;
  description?: string | null;
  quantity?: string | number | null;
  unitPrice?: string | null;
  totalPrice?: string | null;
}

interface InvoiceLineItemsProps {
  invoiceId: string;
  lineItems: InvoiceLineItemDisplay[];
  currency?: string;
  showMatching?: boolean;
  canEditMatches?: boolean;
}

interface AssociatedDocument {
  entity: {
    entityId: string;
    entityType: SystemEntity;
  };
  associationType?: string | null;
}

interface MatchingCellProps {
  lineItemId: string;
  lineItem: InvoiceLineItemDisplay;
  canEditMatches: boolean;
  associatedPOs: AssociatedDocument[];
  associatedSOs: AssociatedDocument[];
  associatedShipments: AssociatedDocument[];
  // Pre-fetched associations for this line item to avoid N+1 queries
  lineItemAssociations?: Array<{
    entity: {
      entityId: string;
      entityType: SystemEntity;
    };
    associationType?: EntityAssociationType | null;
  }>;
  allMatchedPOLineItemIds: Set<string>;
  allMatchedChargeIds: Set<string>;
  // Pre-fetched matched PO line item data
  matchedPOLineItem?: PurchaseOrderLineItem;
  // Pre-fetched matched charge data
  matchedCharge?: Charge;
  currency?: string;
}

// Helper function to get charge display name with fallback chain
function getChargeDisplayName(
  charge: Charge | undefined,
  fallback = 'Unknown Charge',
): string {
  return (
    charge?.chargeCategory?.label ||
    charge?.chargeCategory?.value ||
    charge?.sourceReference ||
    fallback
  );
}

// Helper function to compare two numeric values with tolerance
function valuesDiffer(
  val1: string | number | null | undefined,
  val2: string | number | null | undefined,
): boolean {
  const num1 = val1 === null || val1 === undefined ? 0 : Number(val1);
  const num2 = val2 === null || val2 === undefined ? 0 : Number(val2);
  if (Number.isNaN(num1) || Number.isNaN(num2)) {
    return String(val1) !== String(val2);
  }
  // Use small tolerance for floating point comparison
  return Math.abs(num1 - num2) > 0.01;
}

function getLineItemDifferenceMessages({
  invoiceLineItem,
  poLineItem,
  currency = 'USD',
}: {
  invoiceLineItem?: InvoiceLineItemDisplay;
  poLineItem?: PurchaseOrderLineItem | null;
  currency?: string;
}): string[] {
  if (!invoiceLineItem || !poLineItem) {
    return [];
  }

  const messages: string[] = [];

  if (valuesDiffer(invoiceLineItem.quantity, poLineItem.quantity)) {
    messages.push(
      `Invoice qty ${formatQuantity(invoiceLineItem.quantity)} vs PO qty ${formatQuantity(poLineItem.quantity)}`,
    );
  }

  if (valuesDiffer(invoiceLineItem.unitPrice, poLineItem.unitPrice)) {
    const poUnitPrice =
      poLineItem.unitPrice === null || poLineItem.unitPrice === undefined
        ? null
        : String(poLineItem.unitPrice);
    messages.push(
      `Invoice unit ${formatCurrency(invoiceLineItem.unitPrice, currency)} vs PO unit ${formatCurrency(poUnitPrice, currency)}`,
    );
  }

  if (valuesDiffer(invoiceLineItem.totalPrice, poLineItem.totalPrice)) {
    const poTotalPrice =
      poLineItem.totalPrice === null || poLineItem.totalPrice === undefined
        ? null
        : String(poLineItem.totalPrice);
    messages.push(
      `Invoice total ${formatCurrency(invoiceLineItem.totalPrice, currency)} vs PO total ${formatCurrency(poTotalPrice, currency)}`,
    );
  }

  return messages;
}

function DifferenceInfoIcon({
  messages,
  className,
}: {
  messages: string[];
  className?: string;
}) {
  if (!messages.length) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label="View line item discrepancies"
          className={cn(
            'flex h-4 w-4 items-center justify-center text-muted-foreground hover:text-foreground',
            className,
          )}
        >
          <InfoIcon className="h-3 w-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-1 text-xs">
        {messages.map((message) => (
          <p key={message}>{message}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

// Component to display a single PO line item in compact view
function POLineItemCompact({
  lineItem,
  invoiceLineItem,
  isMatched,
  isDisabled,
  onSelect,
  currency = 'USD',
}: {
  lineItem: PurchaseOrderLineItem;
  invoiceLineItem?: InvoiceLineItemDisplay;
  isMatched: boolean;
  isDisabled: boolean;
  onSelect: () => void;
  currency?: string;
}) {
  const displayName = lineItem.description || 'No description';

  // Compare values if invoice line item is provided
  const qtyDiffers =
    invoiceLineItem &&
    valuesDiffer(invoiceLineItem.quantity, lineItem.quantity);
  const unitPriceDiffers =
    invoiceLineItem &&
    valuesDiffer(invoiceLineItem.unitPrice, lineItem.unitPrice);
  const totalPriceDiffers =
    invoiceLineItem &&
    valuesDiffer(invoiceLineItem.totalPrice, lineItem.totalPrice);
  const differenceMessages = invoiceLineItem
    ? getLineItemDifferenceMessages({
        invoiceLineItem,
        poLineItem: lineItem,
        currency,
      })
    : [];

  return (
    <CommandItem
      key={lineItem.id}
      onSelect={onSelect}
      disabled={isDisabled}
      className={cn(
        'flex items-start gap-2 py-2',
        isDisabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <CheckIcon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          isMatched ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{displayName}</span>
          <DifferenceInfoIcon messages={differenceMessages} />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className={qtyDiffers ? 'text-red-600' : ''}>
            Qty: {lineItem.quantity ?? '—'}
          </span>
          <span className={unitPriceDiffers ? 'text-red-600' : ''}>
            @ {formatCurrency(lineItem.unitPrice, currency)}
          </span>
          <span
            className={cn('font-medium', totalPriceDiffers && 'text-red-600')}
          >
            {formatCurrency(lineItem.totalPrice, currency)}
          </span>
        </div>
      </div>
    </CommandItem>
  );
}

// Component to display a single charge in compact view (for any document type)
function ChargeCompact({
  charge,
  invoiceLineItem,
  isMatched,
  onSelect,
  showParentDocument = false,
}: {
  charge: Charge;
  invoiceLineItem?: InvoiceLineItemDisplay;
  isMatched: boolean;
  onSelect: () => void;
  showParentDocument?: boolean;
}) {
  // Get charge category label (longer description) or fall back to value/sourceReference
  const displayName = getChargeDisplayName(charge);

  const chargeAmount = Number(charge.amount);

  // Compare amount if invoice line item is provided
  const invoiceAmount = invoiceLineItem?.totalPrice
    ? Number(invoiceLineItem.totalPrice)
    : null;
  const amountDiffers =
    invoiceAmount !== null && Math.abs(chargeAmount - invoiceAmount) > 0.01;

  return (
    <CommandItem
      key={charge.id}
      onSelect={onSelect}
      className="flex items-start gap-2 py-2"
    >
      <CheckIcon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          isMatched ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-1.5">
          <GitDiffIcon className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{displayName}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className={amountDiffers ? 'text-red-600 font-medium' : ''}>
            {chargeAmount.toLocaleString(undefined, {
              style: 'currency',
              currency: charge.currencyCode || 'USD',
              minimumFractionDigits: 2,
            })}
          </span>
          <span className="text-muted-foreground/60">
            {charge.direction === 'DEBIT' ? 'Debit' : 'Credit'}
          </span>
          {showParentDocument && charge.docType && charge.docId && (
            <SystemEntityInline
              entityType={charge.docType as SystemEntity}
              entityId={charge.docId}
              className="text-[10px] h-4 py-0"
            />
          )}
        </div>
      </div>
    </CommandItem>
  );
}

// Component to fetch and display charges for any document type (PO, SO, Shipment)
function DocumentChargesSection({
  docType,
  docId,
  matchedChargeId,
  alreadyMatchedChargeIds,
  currentMatchedChargeId,
  invoiceLineItem,
  onSelectCharge,
  label,
  icon: Icon = CurrencyDollarIcon,
}: {
  docType: SystemEntity;
  docId: string;
  matchedChargeId: string | null;
  alreadyMatchedChargeIds: Set<string>;
  currentMatchedChargeId: string | null;
  invoiceLineItem?: InvoiceLineItemDisplay;
  onSelectCharge: (chargeId: string) => void;
  label?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  const { charges, isLoading } = useChargesForEntity({
    entityType: docType as ChargeableEntity,
    entityId: docId,
    includeLineCharges: false, // Header charges only for matching
    enabled: !!docId,
  });

  if (isLoading) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        Loading charges...
      </div>
    );
  }

  if (!charges || charges.length === 0) {
    return null; // Don't show anything if no charges
  }

  // Filter out charges that are already matched to OTHER invoice line items
  const unmatchedCharges = charges.filter((charge) => {
    // Always show the currently matched charge for this line item
    if (currentMatchedChargeId === charge.id) {
      return true;
    }
    // For other charges, only show if not matched
    return !alreadyMatchedChargeIds.has(charge.id);
  });

  if (unmatchedCharges.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      <div className="px-2 py-1 text-xs font-medium text-muted-foreground flex items-center gap-1.5">
        <Icon className="h-3 w-3" />
        {label || 'Charges'}
      </div>
      {unmatchedCharges.map((charge) => (
        <ChargeCompact
          key={charge.id}
          charge={charge}
          invoiceLineItem={invoiceLineItem}
          isMatched={matchedChargeId === charge.id}
          onSelect={() => onSelectCharge(charge.id)}
        />
      ))}
    </div>
  );
}

// Backwards compatible alias for PO charges
function POChargesSection(props: {
  poId: string;
  matchedChargeId: string | null;
  alreadyMatchedChargeIds: Set<string>;
  currentMatchedChargeId: string | null;
  invoiceLineItem?: InvoiceLineItemDisplay;
  onSelectCharge: (chargeId: string) => void;
}) {
  return (
    <DocumentChargesSection
      docType={SystemEntity.INTERNAL_PURCHASE_ORDER}
      docId={props.poId}
      matchedChargeId={props.matchedChargeId}
      alreadyMatchedChargeIds={props.alreadyMatchedChargeIds}
      currentMatchedChargeId={props.currentMatchedChargeId}
      invoiceLineItem={props.invoiceLineItem}
      onSelectCharge={props.onSelectCharge}
    />
  );
}

// Component to fetch and display PO details with line items
function POLineItemsSection({
  poId,
  matchedLineItemId,
  alreadyMatchedPOLineItemIds,
  currentLineItemMatchedId,
  invoiceLineItem,
  onSelectLineItem,
  currency,
}: {
  poId: string;
  matchedLineItemId: string | null;
  alreadyMatchedPOLineItemIds: Set<string>;
  currentLineItemMatchedId: string | null;
  invoiceLineItem?: InvoiceLineItemDisplay;
  onSelectLineItem: (lineItemId: string) => void;
  currency?: string;
}) {
  const { purchaseOrder, data, isLoading } = usePurchaseOrder(poId, !!poId);

  if (isLoading) {
    return (
      <div className="px-2 py-3 text-sm text-muted-foreground">
        Loading line items...
      </div>
    );
  }

  if (!data?.lineItems || data.lineItems.length === 0) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        No line items found
      </div>
    );
  }

  // Filter out line items that are already matched to OTHER invoice line items
  // But include the current line item's match (if any) so it shows in the list
  const unmatchedLineItems = data.lineItems.filter((lineItem) => {
    // Always show the currently matched item for this line item (so you can see what's matched and unmatch it)
    if (currentLineItemMatchedId === lineItem.id) {
      return true;
    }
    // For all other items, only show if they're NOT matched to any invoice line item
    return !alreadyMatchedPOLineItemIds.has(lineItem.id);
  });

  if (unmatchedLineItems.length === 0) {
    return (
      <div className="px-2 py-2 text-xs text-muted-foreground">
        No unmatched line items
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {unmatchedLineItems.map((lineItem) => (
        <POLineItemCompact
          key={lineItem.id}
          lineItem={lineItem}
          invoiceLineItem={invoiceLineItem}
          isMatched={matchedLineItemId === lineItem.id}
          isDisabled={false}
          onSelect={() => onSelectLineItem(lineItem.id)}
          currency={currency}
        />
      ))}
    </div>
  );
}

// Separate component for the matching cell to handle its own hooks and state
function MatchingCell({
  lineItemId,
  lineItem,
  canEditMatches,
  associatedPOs,
  associatedSOs,
  associatedShipments,
  lineItemAssociations = [],
  allMatchedPOLineItemIds,
  allMatchedChargeIds,
  matchedPOLineItem,
  matchedCharge,
  currency,
}: MatchingCellProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const invoiceCurrency = currency || 'USD';

  const createMutation = useCreateEntityAssociation();
  const deleteMutation = useDeleteEntityAssociation();

  // Find the matched PO line item from pre-fetched associations
  const lineItemMatch =
    lineItemAssociations.find(
      (assoc) =>
        assoc.entity.entityType ===
        SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
    ) || null;

  // Find matched charge from pre-fetched associations
  const chargeMatch =
    lineItemAssociations.find(
      (assoc) => assoc.entity.entityType === SystemEntity.CHARGE,
    ) || null;

  // Determine which type of match we have (line item takes precedence in display)
  const match = lineItemMatch || chargeMatch;
  const isChargeMatch = !lineItemMatch && !!chargeMatch;

  const matchedDescription = matchedPOLineItem?.description || 'Unnamed item';

  // Check if values differ between invoice line item and matched PO line item
  const hasDifferences = useMemo(() => {
    if (!matchedPOLineItem) return false;

    return (
      valuesDiffer(lineItem.quantity, matchedPOLineItem.quantity) ||
      valuesDiffer(lineItem.unitPrice, matchedPOLineItem.unitPrice) ||
      valuesDiffer(lineItem.totalPrice, matchedPOLineItem.totalPrice)
    );
  }, [lineItem, matchedPOLineItem]);

  const differenceMessages = useMemo(() => {
    return getLineItemDifferenceMessages({
      invoiceLineItem: lineItem,
      poLineItem: matchedPOLineItem,
      currency: invoiceCurrency,
    });
  }, [invoiceCurrency, lineItem, matchedPOLineItem]);

  // Build a set of matched PO line item IDs excluding the current line item's match
  // This ensures that when you unmatch, the item becomes available again
  const otherMatchedPOLineItemIds = useMemo(() => {
    const otherMatched = new Set(allMatchedPOLineItemIds);
    // Remove the current match so it shows up in the list even if it was matched
    if (lineItemMatch?.entity.entityId) {
      otherMatched.delete(lineItemMatch.entity.entityId);
    }
    return otherMatched;
  }, [allMatchedPOLineItemIds, lineItemMatch?.entity.entityId]);

  // Build a set of matched charge IDs excluding the current line item's match
  const otherMatchedChargeIds = useMemo(() => {
    const otherMatched = new Set(allMatchedChargeIds);
    if (chargeMatch?.entity.entityId) {
      otherMatched.delete(chargeMatch.entity.entityId);
    }
    return otherMatched;
  }, [allMatchedChargeIds, chargeMatch?.entity.entityId]);

  const handleCreateLineItemMatch = async (poLineItemId: string) => {
    // If there's an existing match, delete it first
    if (match) {
      await deleteMutation.mutateAsync({
        sourceEntityId: lineItemId,
        sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
        targetEntityId: match.entity.entityId,
        targetEntityType: match.entity.entityType,
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
      });
    }

    // Create the new match
    await createMutation.mutateAsync({
      sourceEntityId: lineItemId,
      sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
      targetEntityId: poLineItemId,
      targetEntityType: SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
    setIsPopoverOpen(false);
  };

  const handleCreateChargeMatch = async (chargeId: string) => {
    // If there's an existing match, delete it first
    if (match) {
      await deleteMutation.mutateAsync({
        sourceEntityId: lineItemId,
        sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
        targetEntityId: match.entity.entityId,
        targetEntityType: match.entity.entityType,
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
      });
    }

    // Create the new match to charge
    await createMutation.mutateAsync({
      sourceEntityId: lineItemId,
      sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
      targetEntityId: chargeId,
      targetEntityType: SystemEntity.CHARGE,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
    setIsPopoverOpen(false);
  };

  const handleDeleteMatch = async () => {
    if (match) {
      await deleteMutation.mutateAsync({
        sourceEntityId: lineItemId,
        sourceEntityType: SystemEntity.AP_INVOICE_LINE_ITEM,
        targetEntityId: match.entity.entityId,
        targetEntityType: match.entity.entityType,
        associationType: EntityAssociationType.AP_INVOICE_MATCHING,
      });
    }
  };

  // Get display name for matched charge
  const matchedChargeDescription = getChargeDisplayName(
    matchedCharge,
    'Charge',
  );

  // Get parent document info from matched charge
  const chargeParentDocType = matchedCharge?.docType as
    | SystemEntity
    | undefined;
  const chargeParentDocId = matchedCharge?.docId;

  if (!canEditMatches) {
    // Read-only display
    if (isChargeMatch) {
      return (
        <div className="flex items-center gap-2 border rounded-md px-2 py-1 max-w-[180px] border-green-500 bg-green-50">
          <GitDiffIcon className="h-4 w-4 text-green-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">
              {matchedChargeDescription}
            </div>
            {chargeParentDocType && chargeParentDocId && (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <span>from</span>
                <SystemEntityInline
                  entityType={chargeParentDocType}
                  entityId={chargeParentDocId}
                  className="text-[10px] h-4 py-0"
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    return match ? (
      <div
        className={cn(
          'flex items-center gap-2 border rounded-md px-2 py-1 max-w-[150px]',
          hasDifferences
            ? 'border-yellow-500 bg-yellow-50'
            : 'border-green-500 bg-green-50',
        )}
      >
        {hasDifferences ? (
          <WarningIcon className="h-4 w-4 text-yellow-600 shrink-0" />
        ) : (
          <CheckIcon className="h-4 w-4 text-green-600 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate">
            {matchedDescription}
          </div>
        </div>
        <DifferenceInfoIcon
          messages={differenceMessages}
          className="ml-1 shrink-0"
        />
      </div>
    ) : (
      <span className="text-xs text-muted-foreground">Not matched</span>
    );
  }

  // Interactive matching
  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
      <PopoverTrigger asChild>
        {match ? (
          isChargeMatch ? (
            // Charge match display
            <div className="flex items-center gap-2 border rounded-md px-2 py-1 transition-colors cursor-pointer min-w-0 max-w-[180px] border-green-500 bg-green-50 hover:bg-green-100">
              <GitDiffIcon className="h-4 w-4 text-green-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">
                  {matchedChargeDescription}
                </div>
                {chargeParentDocType && chargeParentDocId && (
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <span>from</span>
                    <SystemEntityInline
                      entityType={chargeParentDocType}
                      entityId={chargeParentDocId}
                      className="text-[10px] h-4 py-0"
                    />
                  </div>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DotsThreeIcon className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMatch();
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    Remove match
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            // Line item match display
            <div
              className={cn(
                'flex items-center gap-2 border rounded-md px-2 py-1 transition-colors cursor-pointer min-w-0 max-w-[150px]',
                hasDifferences
                  ? 'border-yellow-500 bg-yellow-50 hover:bg-yellow-100'
                  : 'border-green-500 bg-green-50 hover:bg-green-100',
              )}
            >
              {hasDifferences ? (
                <WarningIcon className="h-4 w-4 text-yellow-600 shrink-0" />
              ) : (
                <CheckIcon className="h-4 w-4 text-green-600 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">
                  {matchedDescription}
                </div>
              </div>
              <DifferenceInfoIcon
                messages={differenceMessages}
                className="ml-1 shrink-0"
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DotsThreeIcon className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMatch();
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    Remove match
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        ) : (
          <Button variant="outline" size="sm" className="h-7 text-xs">
            <PlusIcon className="h-3 w-3 mr-1" />
            Match
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search line items or charges..." />
          <CommandList>
            <CommandEmpty>
              {associatedPOs.length === 0 &&
              associatedSOs.length === 0 &&
              associatedShipments.length === 0
                ? 'No associated documents found. Link an invoice to a PO, SO, or Shipment first.'
                : 'No matching items found.'}
            </CommandEmpty>
            {/* Purchase Orders - with line items and charges */}
            {associatedPOs.map((po, index) => {
              const poId = po.entity.entityId;
              return (
                <React.Fragment key={poId}>
                  {index > 0 && <CommandSeparator />}
                  <CommandGroup
                    heading={
                      <div className="flex items-center gap-2">
                        <PackageIcon className="h-3 w-3" />
                        <SystemEntityInline
                          entityType={po.entity.entityType}
                          entityId={po.entity.entityId}
                          className="text-xs"
                        />
                      </div>
                    }
                  >
                    <POLineItemsSection
                      poId={poId}
                      matchedLineItemId={lineItemMatch?.entity.entityId || null}
                      alreadyMatchedPOLineItemIds={otherMatchedPOLineItemIds}
                      currentLineItemMatchedId={
                        lineItemMatch?.entity.entityId || null
                      }
                      invoiceLineItem={lineItem}
                      onSelectLineItem={handleCreateLineItemMatch}
                      currency={invoiceCurrency}
                    />
                    <POChargesSection
                      poId={poId}
                      matchedChargeId={chargeMatch?.entity.entityId || null}
                      alreadyMatchedChargeIds={otherMatchedChargeIds}
                      currentMatchedChargeId={
                        chargeMatch?.entity.entityId || null
                      }
                      invoiceLineItem={lineItem}
                      onSelectCharge={handleCreateChargeMatch}
                    />
                  </CommandGroup>
                </React.Fragment>
              );
            })}
            {/* Sales Orders - charges only */}
            {associatedSOs.length > 0 && associatedPOs.length > 0 && (
              <CommandSeparator />
            )}
            {associatedSOs.map((so, index) => {
              const soId = so.entity.entityId;
              return (
                <React.Fragment key={soId}>
                  {index > 0 && <CommandSeparator />}
                  <CommandGroup
                    heading={
                      <div className="flex items-center gap-2">
                        <ShoppingCartIcon className="h-3 w-3" />
                        <SystemEntityInline
                          entityType={so.entity.entityType}
                          entityId={so.entity.entityId}
                          className="text-xs"
                        />
                      </div>
                    }
                  >
                    <DocumentChargesSection
                      docType={SystemEntity.AR_SALES_ORDER}
                      docId={soId}
                      matchedChargeId={chargeMatch?.entity.entityId || null}
                      alreadyMatchedChargeIds={otherMatchedChargeIds}
                      currentMatchedChargeId={
                        chargeMatch?.entity.entityId || null
                      }
                      invoiceLineItem={lineItem}
                      onSelectCharge={handleCreateChargeMatch}
                      label="SO Charges"
                      icon={ShoppingCartIcon}
                    />
                  </CommandGroup>
                </React.Fragment>
              );
            })}
            {/* Shipments - charges only */}
            {associatedShipments.length > 0 &&
              (associatedPOs.length > 0 || associatedSOs.length > 0) && (
                <CommandSeparator />
              )}
            {associatedShipments.map((shipment, index) => {
              const shipmentId = shipment.entity.entityId;
              return (
                <React.Fragment key={shipmentId}>
                  {index > 0 && <CommandSeparator />}
                  <CommandGroup
                    heading={
                      <div className="flex items-center gap-2">
                        <TruckIcon className="h-3 w-3" />
                        <SystemEntityInline
                          entityType={shipment.entity.entityType}
                          entityId={shipment.entity.entityId}
                          className="text-xs"
                        />
                      </div>
                    }
                  >
                    <DocumentChargesSection
                      docType={SystemEntity.SCM_SHIPMENT}
                      docId={shipmentId}
                      matchedChargeId={chargeMatch?.entity.entityId || null}
                      alreadyMatchedChargeIds={otherMatchedChargeIds}
                      currentMatchedChargeId={
                        chargeMatch?.entity.entityId || null
                      }
                      invoiceLineItem={lineItem}
                      onSelectCharge={handleCreateChargeMatch}
                      label="Shipment Charges"
                      icon={TruckIcon}
                    />
                  </CommandGroup>
                </React.Fragment>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Hook to batch fetch associations for all line items and return a map
 * This avoids N+1 queries by fetching all associations upfront
 */
function useLineItemAssociationsMap(lineItems: InvoiceLineItemDisplay[]) {
  // Fetch all line item associations upfront using useQueries
  const lineItemQueries = useQueries({
    queries: useMemo(
      () =>
        lineItems.map((lineItem) =>
          orpc.core.entityAssociations.list.queryOptions({
            input: {
              entityId: lineItem.id,
              entityType: SystemEntity.AP_INVOICE_LINE_ITEM,
              associationType: EntityAssociationType.AP_INVOICE_MATCHING,
            },
          }),
        ),
      [lineItems],
    ),
  });

  // Build a map of line item ID to its associations
  // Create a stable key based on actual data to avoid infinite loops
  const dataKey = lineItemQueries
    .map((q) => `${q.dataUpdatedAt}-${q.data?.associations.length ?? 0}`)
    .join('|');

  const lineItemAssociationsMap = useMemo(() => {
    const map = new Map<
      string,
      Array<{
        entity: { entityId: string; entityType: SystemEntity };
        associationType?: EntityAssociationType | null;
      }>
    >();

    lineItems.forEach((lineItem, index) => {
      const queryResult = lineItemQueries[index];
      if (queryResult.data?.associations) {
        map.set(lineItem.id, queryResult.data.associations);
      } else {
        map.set(lineItem.id, []);
      }
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, dataKey]);

  return lineItemAssociationsMap;
}

function formatQuantity(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }

  // Format with max 4 decimal places and truncate trailing zeros
  const formatted = numeric.toFixed(4);
  // Remove trailing zeros and unnecessary decimal point
  return formatted.replace(/\.?0+$/, '');
}

function formatCurrency(value?: string | null, currency = 'USD') {
  if (!value) {
    return '—';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
}

export function InvoiceLineItems({
  invoiceId,
  lineItems,
  currency,
  showMatching = true,
  canEditMatches = true,
}: InvoiceLineItemsProps) {
  // Fetch invoice associations to get associated documents (POs, SOs, Shipments)
  const invoiceAssociationsQuery = useEntityAssociationsList({
    entityId: invoiceId,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });

  // Get associated POs (for line item matching)
  const associatedPOs = useMemo(() => {
    const associations = invoiceAssociationsQuery.data?.associations || [];
    // Filter to only PO-level associations (not line items)
    const poAssociations = associations.filter(
      (assoc) =>
        assoc.entity.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER,
    );
    // Deduplicate by entity ID
    const seen = new Set<string>();
    return poAssociations.filter((assoc) => {
      if (seen.has(assoc.entity.entityId)) {
        return false;
      }
      seen.add(assoc.entity.entityId);
      return true;
    });
  }, [invoiceAssociationsQuery.data?.associations]);

  // Get associated Sales Orders (for charge matching)
  const associatedSOs = useMemo(() => {
    const associations = invoiceAssociationsQuery.data?.associations || [];
    const soAssociations = associations.filter(
      (assoc) => assoc.entity.entityType === SystemEntity.AR_SALES_ORDER,
    );
    const seen = new Set<string>();
    return soAssociations.filter((assoc) => {
      if (seen.has(assoc.entity.entityId)) {
        return false;
      }
      seen.add(assoc.entity.entityId);
      return true;
    });
  }, [invoiceAssociationsQuery.data?.associations]);

  // Get associated Shipments (for charge matching)
  const associatedShipments = useMemo(() => {
    const associations = invoiceAssociationsQuery.data?.associations || [];
    const shipmentAssociations = associations.filter(
      (assoc) => assoc.entity.entityType === SystemEntity.SCM_SHIPMENT,
    );
    const seen = new Set<string>();
    return shipmentAssociations.filter((assoc) => {
      if (seen.has(assoc.entity.entityId)) {
        return false;
      }
      seen.add(assoc.entity.entityId);
      return true;
    });
  }, [invoiceAssociationsQuery.data?.associations]);

  // Get list of associated PO IDs for shipping document queries
  const associatedPOIds = useMemo(() => {
    return associatedPOs.map((po) => po.entity.entityId);
  }, [associatedPOs]);

  // Fetch shipping documents for all associated POs
  const shippingDocQueries = useQueries({
    queries: useMemo(
      () =>
        associatedPOIds.map((poId) =>
          orpc.supplyChainManagement.shippingDocuments.byPurchaseOrder.queryOptions(
            {
              input: { purchaseOrderId: poId },
            },
          ),
        ),
      [associatedPOIds],
    ),
  });

  // Build a map from PO line item ID → shipping document line item(s)
  const shippingDocDataKey = shippingDocQueries
    .map((q) => `${q.dataUpdatedAt}-${q.data?.length ?? 0}`)
    .join('|');

  interface ShippingDocLineItem {
    id: string;
    internalPurchaseOrderLineItemId?: string | null;
    expectedQuantity?: number | null;
    actualQuantity?: number | null;
    description?: string | null;
  }

  const poLineItemToShippingDocMap = useMemo(() => {
    const map = new Map<string, ShippingDocLineItem>();
    shippingDocQueries.forEach((query) => {
      const docs = query.data || [];
      docs.forEach((doc) => {
        const lineItems = (doc.lineItems || []) as ShippingDocLineItem[];
        lineItems.forEach((lineItem) => {
          if (lineItem.internalPurchaseOrderLineItemId) {
            map.set(lineItem.internalPurchaseOrderLineItemId, lineItem);
          }
        });
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingDocDataKey]);

  // Fetch all line item associations upfront to avoid N+1 queries
  const lineItemAssociationsMap = useLineItemAssociationsMap(lineItems);

  // Fetch all matched PO line items to compare values
  const matchedPOLineItemQueries = useQueries({
    queries: useMemo(
      () =>
        lineItems.map((lineItem) => {
          const associations = lineItemAssociationsMap.get(lineItem.id) || [];
          const match = associations.find(
            (assoc) =>
              assoc.entity.entityType ===
              SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
          );

          return orpc.accountsPayable.purchaseOrders.getLineItem.queryOptions({
            input: { id: match?.entity.entityId || '' },
            enabled: !!match?.entity.entityId,
          });
        }),
      [lineItems, lineItemAssociationsMap],
    ),
  });

  // Fetch all matched charges to display charge category info
  const matchedChargeQueries = useQueries({
    queries: useMemo(
      () =>
        lineItems.map((lineItem) => {
          const associations = lineItemAssociationsMap.get(lineItem.id) || [];
          const match = associations.find(
            (assoc) => assoc.entity.entityType === SystemEntity.CHARGE,
          );

          return orpc.finance.charges.getById.queryOptions({
            input: { id: match?.entity.entityId || '' },
            enabled: !!match?.entity.entityId,
          });
        }),
      [lineItems, lineItemAssociationsMap],
    ),
  });

  // Build a map of invoice line item ID to matched PO line item
  // Create a stable key based on actual data to avoid infinite loops
  const matchedDataKey = matchedPOLineItemQueries
    .map((q) => `${q.dataUpdatedAt}-${q.data?.lineItem?.id ?? ''}`)
    .join('|');

  const matchedPOLineItemsMap = useMemo(() => {
    const map = new Map<string, PurchaseOrderLineItem>();
    lineItems.forEach((lineItem, index) => {
      const queryResult = matchedPOLineItemQueries[index];
      if (queryResult.data?.lineItem) {
        map.set(lineItem.id, queryResult.data.lineItem);
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, matchedDataKey]);

  // Build a map of invoice line item ID to matched charge
  const matchedChargeDataKey = matchedChargeQueries
    .map((q) => `${q.dataUpdatedAt}-${q.data?.charge?.id ?? ''}`)
    .join('|');

  const matchedChargesMap = useMemo(() => {
    const map = new Map<string, Charge>();
    lineItems.forEach((lineItem, index) => {
      const queryResult = matchedChargeQueries[index];
      if (queryResult.data?.charge) {
        map.set(lineItem.id, queryResult.data.charge);
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, matchedChargeDataKey]);

  // Define columns
  const columns = useMemo<ColumnDef<InvoiceLineItemDisplay>[]>(() => {
    // Build a set of all PO line item IDs that are already matched to any invoice line item
    const allMatchedPOLineItemIds = new Set<string>();
    // Build a set of all charge IDs that are already matched to any invoice line item
    const allMatchedChargeIds = new Set<string>();

    lineItemAssociationsMap.forEach((associations) => {
      associations.forEach((assoc) => {
        if (
          assoc.entity.entityType ===
          SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM
        ) {
          allMatchedPOLineItemIds.add(assoc.entity.entityId);
        } else if (assoc.entity.entityType === SystemEntity.CHARGE) {
          allMatchedChargeIds.add(assoc.entity.entityId);
        }
      });
    });

    const baseColumns: ColumnDef<InvoiceLineItemDisplay>[] = [
      {
        id: 'lineNumber',
        accessorKey: 'lineNumber',
        header: () => <div className="text-center">#</div>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="text-center font-medium text-muted-foreground">
              {row.lineNumber}
            </div>
          );
        },
        size: 48,
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Description',
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="font-medium">
              {row.description || 'No description'}
            </div>
          );
        },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: () => <div className="text-right">Qty</div>,
        cell: (info) => {
          const row = info.row.original;
          const matchedPOLineItem = matchedPOLineItemsMap.get(row.id);
          const hasDifference =
            matchedPOLineItem &&
            valuesDiffer(row.quantity, matchedPOLineItem.quantity);

          return (
            <div
              className={cn(
                'text-right',
                hasDifference && 'text-yellow-700 font-semibold',
              )}
            >
              {formatQuantity(row.quantity)}
            </div>
          );
        },
        size: 80,
      },
      {
        id: 'unitPrice',
        accessorKey: 'unitPrice',
        header: () => <div className="text-right">Unit Price</div>,
        cell: (info) => {
          const row = info.row.original;
          const matchedPOLineItem = matchedPOLineItemsMap.get(row.id);
          const hasDifference =
            matchedPOLineItem &&
            valuesDiffer(row.unitPrice, matchedPOLineItem.unitPrice);

          return (
            <div
              className={cn(
                'text-right text-muted-foreground',
                hasDifference && 'text-yellow-700 font-semibold',
              )}
            >
              {formatCurrency(row.unitPrice, currency)}
            </div>
          );
        },
        size: 112,
      },
      {
        id: 'totalPrice',
        accessorKey: 'totalPrice',
        header: () => <div className="text-right">Total</div>,
        cell: (info) => {
          const row = info.row.original;
          const matchedPOLineItem = matchedPOLineItemsMap.get(row.id);
          const hasDifference =
            matchedPOLineItem &&
            valuesDiffer(row.totalPrice, matchedPOLineItem.totalPrice);

          return (
            <div
              className={cn(
                'text-right font-semibold',
                hasDifference && 'text-yellow-700',
              )}
            >
              {formatCurrency(row.totalPrice, currency)}
            </div>
          );
        },
        size: 112,
      },
    ];

    if (showMatching) {
      baseColumns.push({
        id: 'matching',
        header: 'Order Match',
        cell: (info) => {
          const row = info.row.original;
          return (
            <MatchingCell
              lineItemId={row.id}
              lineItem={row}
              canEditMatches={canEditMatches}
              associatedPOs={associatedPOs}
              associatedSOs={associatedSOs}
              associatedShipments={associatedShipments}
              lineItemAssociations={lineItemAssociationsMap.get(row.id)}
              allMatchedPOLineItemIds={allMatchedPOLineItemIds}
              allMatchedChargeIds={allMatchedChargeIds}
              matchedPOLineItem={matchedPOLineItemsMap.get(row.id)}
              matchedCharge={matchedChargesMap.get(row.id)}
              currency={currency}
            />
          );
        },
        size: 150,
      });

      baseColumns.push({
        id: 'deliveryMatch',
        header: 'Delivery',
        cell: (info) => {
          const row = info.row.original;
          const matchedPOLineItem = matchedPOLineItemsMap.get(row.id);
          if (!matchedPOLineItem) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }

          const shippingDocLineItem = poLineItemToShippingDocMap.get(
            matchedPOLineItem.id,
          );
          if (!shippingDocLineItem) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }

          const expectedQty = shippingDocLineItem.expectedQuantity ?? 0;
          const actualQty = shippingDocLineItem.actualQuantity ?? 0;
          const hasDiscrepancy = actualQty !== expectedQty;

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1 rounded text-xs',
                    hasDiscrepancy
                      ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                      : 'bg-green-50 text-green-700 border border-green-200',
                  )}
                >
                  <TruckIcon className="h-3 w-3" />
                  <span>
                    {actualQty}/{expectedQty}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs space-y-1">
                  <div>Expected: {expectedQty}</div>
                  <div>Received: {actualQty}</div>
                  {hasDiscrepancy && (
                    <div className="text-yellow-600">Quantity discrepancy</div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        },
        size: 100,
      });
    }

    return baseColumns;
  }, [
    currency,
    showMatching,
    canEditMatches,
    associatedPOs,
    associatedSOs,
    associatedShipments,
    lineItemAssociationsMap,
    matchedPOLineItemsMap,
    matchedChargesMap,
    poLineItemToShippingDocMap,
  ]);

  const table = useReactTable({
    data: lineItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  // Calculate totals for the footer row
  const totals = useMemo(() => {
    let totalQuantity = 0;
    let totalPrice = 0;
    lineItems.forEach((item) => {
      totalQuantity += Number(item.quantity) || 0;
      totalPrice += Number(item.totalPrice) || 0;
    });
    return { totalQuantity, totalPrice };
  }, [lineItems]);

  return (
    <div className="space-y-2">
      <div className="border rounded-md mx-3 overflow-hidden">
        <div className="max-h-96 overflow-y-auto overscroll-y-contain">
          <Table>
            <TableHeader className="sticky top-0 bg-muted/50 backdrop-blur">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{
                        width:
                          header.getSize() !== 150
                            ? `${header.getSize()}px`
                            : undefined,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter className="sticky bottom-0 bg-muted/95 backdrop-blur">
              <TableRow>
                <TableCell />
                <TableCell className="font-semibold">Total</TableCell>
                <TableCell />
                <TableCell />
                <TableCell className="text-right font-semibold">
                  {formatCurrency(String(totals.totalPrice), currency)}
                </TableCell>
                {showMatching && (
                  <>
                    <TableCell />
                    <TableCell />
                  </>
                )}
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </div>
    </div>
  );
}
